import { useEffect, useState } from "react";
import { BRAND_LABEL } from "./copy";
import type { Answers } from "@/schema/quizSchema";

/**
 * The thinking screen, faithful to the approved Manly prototype
 * (docs/specs/2026-07-09-manly-quiz-prototype.html): four rows light up in turn
 * — a gold pulsing ring while a row is active, then its tick fills gold with a
 * check when done. Names the villain from Q3 (the persuasion beat the engine's
 * generic BuildingScreen cannot do). Owns its timers per the renderBuilding
 * contract and MUST clear every one on unmount.
 */
export function BuildingRows({
  answers, onDone, stepMs = 1250,
}: { answers: Answers; onDone: () => void; stepMs?: number }) {
  const brand =
    (typeof answers["current-wash"] === "string" && BRAND_LABEL[answers["current-wash"]]) ||
    "his current wash";

  const rows = [
    "Analyzing his odor pattern",
    "Cross-referencing his age and activity",
    `Checking ${brand} against his skin chemistry`,
    "Personalizing his 2-week reset",
  ];

  // How many rows have completed. 0 = first row active; rows.length = all done.
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timers = rows.map((_, i) =>
      window.setTimeout(() => setActive(i + 1), (i + 1) * stepMs),
    );
    // A short tail after the last tick before advancing, matching the prototype.
    timers.push(window.setTimeout(onDone, rows.length * stepMs + 600));
    return () => timers.forEach((t) => clearTimeout(t));
    // rows is recomputed from answers; the schedule depends only on onDone/stepMs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDone, stepMs]);

  return (
    <div className="qk-manly">
      <div className="qk-manly-loader" role="status" aria-live="polite">
        <h2>Building his Odor Profile…</h2>
        <p className="qk-manly-loader-lead">
          Hold tight while we map your answers against how teen body chemistry actually works.
        </p>

        <ol className="qk-manly-loader-rows">
          {rows.map((r, i) => {
            const state = active > i ? "done" : active === i ? "active" : "pending";
            return (
              <li key={r} className={`qk-manly-lrow is-${state}`}>
                <span className="qk-manly-tick" aria-hidden="true">{state === "done" ? "✓" : ""}</span>
                <span>{r}</span>
              </li>
            );
          })}
        </ol>

        <p className="qk-manly-loader-foot">His profile is almost ready</p>
      </div>
    </div>
  );
}
